/**
 * Contractor Onboarding (P5)
 *
 * Pure resolver + single write boundary for the first-time contractor
 * portal onboarding flow. NO new behavioral logic, NO event emission,
 * NO offer/assignment effects — this is a presentation-state flag.
 *
 * Locked rules:
 *   1. Trigger: onboardingCompletedAt is null AND no offer history AND
 *      no assigned jobs.
 *   2. Both "Skip for now" and "Got it" call markOnboardingComplete —
 *      flag, once set, never re-shows the modal.
 *   3. At deploy time, any contractor with prior offers OR assigned jobs
 *      is backfilled to onboardingCompletedAt = deploy timestamp so
 *      active veterans don't see the tour.
 */

import type { Contractor, Job } from "@/data/types";
import type { MarketplaceOffer } from "./marketplaceOffer";

export type OnboardingReason = "first_login" | "no_history" | "completed";

export interface OnboardingState {
  shouldShow: boolean;
  reason: OnboardingReason;
}

/** Pure resolver. Components MUST NOT compute the trigger inline. */
export function getContractorOnboardingState(
  contractor: Contractor,
  offers: MarketplaceOffer[],
  jobs: Job[],
): OnboardingState {
  if (contractor.onboardingCompletedAt) {
    return { shouldShow: false, reason: "completed" };
  }
  const hasOfferHistory = offers.some(
    (o) => o.contractorId === contractor.id,
  );
  const hasAssignedJobs = jobs.some(
    (j) =>
      j.assignedSubcontractorId === contractor.id ||
      j.assignedSubcontractor === contractor.companyName,
  );
  if (!hasOfferHistory && !hasAssignedJobs) {
    return { shouldShow: true, reason: "first_login" };
  }
  return { shouldShow: false, reason: "no_history" };
}

/** Single canonical write — sets the completion flag. Pure. */
export function markOnboardingComplete(
  contractors: Contractor[],
  contractorId: string,
  at: string = new Date().toISOString(),
): Contractor[] {
  return contractors.map((c) =>
    c.id === contractorId && !c.onboardingCompletedAt
      ? { ...c, onboardingCompletedAt: at }
      : c,
  );
}

/**
 * One-time deploy backfill: any contractor with prior offers or assigned
 * jobs is marked complete so existing veterans never see the tour.
 *
 * Idempotent: contractors who already have onboardingCompletedAt are left
 * untouched. Safe to run on every app boot.
 */
export function backfillOnboardingForActiveContractors(
  contractors: Contractor[],
  offers: MarketplaceOffer[],
  jobs: Job[],
  at: string = new Date().toISOString(),
): Contractor[] {
  const offerSet = new Set(offers.map((o) => o.contractorId));
  const assignedIdSet = new Set(
    jobs.map((j) => j.assignedSubcontractorId).filter(Boolean) as string[],
  );
  const assignedNameSet = new Set(
    jobs.map((j) => j.assignedSubcontractor).filter(Boolean) as string[],
  );
  return contractors.map((c) => {
    if (c.onboardingCompletedAt) return c;
    const hasHistory =
      offerSet.has(c.id) ||
      assignedIdSet.has(c.id) ||
      assignedNameSet.has(c.companyName);
    return hasHistory ? { ...c, onboardingCompletedAt: at } : c;
  });
}
